import { api, queryClient } from '@/lib/api-client.ts'
import { LoaderFunctionArgs } from 'react-router-dom'
import {
  Project,
  ProjectStage,
  ProjectStageLocationsDataT,
  ProjectStageParticipationsDataT,
  ProjectStageParticipationsT,
} from '@/routes/settings/projectDetails/lib/types.ts'
import { getCurrentOrganization } from '@/lib/utils.ts'
import { Issue } from '@/routes/projectStages/issue/types/issue.ts'
import { AxiosResponse } from 'axios'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { getOrganizationIssueTypes } from '@/routes/settings/issueTypes/actions.ts'
import { getOrganizationStatusGroups } from '@/routes/settings/statuses/actions.ts'
import { getOrganizationLabelGroups } from '@/routes/settings/labels/actions.ts'
import { getOrganization } from '@/routes/settings/organization/users/actions.ts'
import { settingsQueryKeys } from '@/lib/queryKeys.ts'

export const projectQuery = (projectId: string | undefined) => ({
  queryKey: ['project', { id: projectId }],
  queryFn: () => getProject(projectId),
})

export const projectsStagesQuery = (projectStageId: string | undefined) => ({
  queryKey: ['projectStage', { id: projectStageId }],
  queryFn: () => getProjectStage(projectStageId),
})

export const projectsStageByIriQuery = (projectStageIri: string | undefined) => ({
  queryKey: ['currStageUsersQuery', { projectStageIri }],
  queryFn: () => getProjectStageByIri(projectStageIri),
})

export const orgProjectStagesQuery = () => ({
  queryKey: ['orgProjectStages'],
  queryFn: () => getOrganizationProjectStages(),
})

export const getCurrentOrganizationQuery = (organizationId: string | undefined) => ({
  queryKey: ['currentOrganization', { organizationId }],
  queryFn: () => getOrganization(),
})

export const loaderSingleProject = async ({ params }: LoaderFunctionArgs) => {
  if (!params.projectId) {
    throw new Error('No project ID provided')
  }

  const { projectId, projectStageId } = params

  // Helper to safely fetch or return cached data
  const safeEnsureQueryData = async <T>(queryOptions: {
    queryKey: unknown[]
    queryFn: () => Promise<T>
  }): Promise<T | undefined> => {
    const cached = queryClient.getQueryData<T>(queryOptions.queryKey)
    if (cached !== undefined) return cached
    if (!navigator.onLine) return undefined
    try {
      return await queryClient.ensureQueryData(queryOptions)
    } catch {
      return undefined
    }
  }

  const safeFetch = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (!navigator.onLine) return undefined
    try {
      return await fn()
    } catch {
      return undefined
    }
  }

  try {
    // Scoped by PROJECT, not by stage: all three fetches take `projectId` alone, and the
    // project-scoped settings editors now live directly under /projects/:projectId (no stage
    // segment). Gating this on `projectStageId` left those editors without their collections —
    // the issue-type dialog reads `statusGroups['hydra:member']` from a non-suspense query and
    // threw on the first render, blanking the screen.
    const [issueTypes, statusGroups, labelGroups] = await Promise.all([
      safeFetch(() => getOrganizationIssueTypes({ projectId })),
      safeFetch(() => getOrganizationStatusGroups({ projectId })),
      safeFetch(() => getOrganizationLabelGroups({ projectId })),
    ])

    await Promise.all([
      safeEnsureQueryData(projectQuery(projectId)),
      safeEnsureQueryData(orgProjectStagesQuery()),
      ...(projectStageId
        ? [
            safeEnsureQueryData(getProjectStageParticipationsQuery(projectStageId)),
            safeEnsureQueryData(getProjectStageLocationsQuery(projectStageId)),
          ]
        : []),
    ])

    return {
      issueTypes,
      statusGroups,
      labelGroups,
      projectId,
      ...(projectStageId && { projectStageId }),
    }
  } catch (e) {
    // Check if this is a network-related error
    const isNetworkError =
      !navigator.onLine ||
      (e instanceof Error &&
        (e.message.toLowerCase().includes('network') ||
          e.message.toLowerCase().includes('failed to fetch') ||
          (e as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      return {
        issueTypes: undefined,
        statusGroups: undefined,
        labelGroups: undefined,
        projectId,
        ...(projectStageId && { projectStageId }),
      }
    }
    return handleApiErr(e)
  }
}
export const getProject = async (projectId: string | undefined) => {
  if (!projectId) return null

  const response = await api.get(`/projects/${projectId}`)
  return response.data as Project
}

export const getProjectStage = async (projectStageId: string | undefined) => {
  if (!projectStageId) return null

  const response = await api.get(`/project_stages/${projectStageId}`)
  return response.data as Project
}

export const getProjectStageByIri = async (projectStageIri: string | undefined) => {
  if (!projectStageIri) return null

  const response = await api.get(`${projectStageIri}`)
  return response.data as ProjectStage
}

export const getOrganizationProjectStages = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id

  if (!organizationId) return null

  const response = await api.get(`/organization/${organizationId}/project_stages?page=1`)
  return response.data as Project
}

export const getOrganizationData = async (organizationId: string) => {
  const response = await api.get(`/organization/${organizationId}/project_stages?page=1`)
  return response.data as Project
}

export const editProjectStageAttachments = async (
  projectStage: Pick<ProjectStage, 'id'> & {
    attachments: (string | undefined)[]
  },
): Promise<AxiosResponse<Issue>> => {
  return api.patch(`/project_stages/${projectStage.id}`, projectStage)
}

export const getProjectStageParticipations = async (
  stageId: string,
): Promise<ProjectStageParticipationsT> => {
  const response: AxiosResponse<ProjectStageParticipationsDataT> = await api.get(
    `/project_stages/${stageId}/project_participations`,
  )
  const stageParticipations: ProjectStageParticipationsT = response.data[`hydra:member`]

  return stageParticipations
}

export const getProjectStageParticipationsQuery = (stageId: string) => ({
  queryKey: settingsQueryKeys.stageParticipations(stageId),
  queryFn: () => getProjectStageParticipations(stageId),
})

export const getProjectStageLocations = async (
  stageId: string | undefined,
): Promise<ProjectStageLocationsDataT['hydra:member']> => {
  const response: AxiosResponse<ProjectStageLocationsDataT> = await api.get(
    `/project_stages/${stageId}/locations`,
  )
  return response.data[`hydra:member`] ?? []
}
export const getProjectStageLocationsQuery = (projectStageId: string | undefined) => ({
  queryKey: ['getProjectStageLocations', { projectStageId }],
  queryFn: () => getProjectStageLocations(projectStageId),
})

export const editProject = async (project: Partial<Project>): Promise<AxiosResponse<Project>> => {
  return api.patch(`/projects/${project.id}`, project)
}

export const editProjectStage = async (
  projectStage: Partial<ProjectStage>,
): Promise<AxiosResponse<ProjectStage>> => {
  return api.patch(`/project_stages/${projectStage.id}`, projectStage)
}

export const deleteLocation = async (locationId: number): Promise<AxiosResponse> => {
  return api.delete(`/locations/${locationId}`)
}

export const addLocation = async (
  location: Partial<ProjectStageLocationsDataT['hydra:member'][0]>,
): Promise<AxiosResponse> => {
  return api.post(`/locations`, location)
}

export const editLocation = async (
  location: Partial<ProjectStageLocationsDataT['hydra:member'][0]>,
): Promise<AxiosResponse> => {
  return api.patch(`/locations/${location.id}`, location)
}

/**
 * One row of a reorder payload: which location, and where the user put it.
 *
 * `position` is the row's index in the list the user is looking at. It is NOT an
 * id and carries no meaning across lists — two locations under different parents
 * both legitimately have position 0.
 */
export type LocationOrderItem = {
  locationId: number
  position: number
}

/**
 * Persist the order the user dragged the stage's floor plans into (issue #816).
 *
 * The trailing slash is load-bearing: `/project_stages/{id}/locations` (no slash)
 * is the GET collection, and it is the slash that separates this collection-level
 * PATCH from the single-resource `PATCH /locations/{id}`.
 *
 * Send the WHOLE list, not just the moved row. The server writes exactly the rows
 * it is given, so an incremental payload would leave every other row holding a
 * position from before the move.
 */
export const updateStageLocationOrdering = async (
  stageId: string | undefined,
  locations: LocationOrderItem[],
): Promise<AxiosResponse> => {
  return api.patch(`/project_stages/${stageId}/locations/`, { locations })
}

/** The same, one level down: the rooms inside one floor plan. */
export const updateChildLocationOrdering = async (
  parentId: number,
  locations: LocationOrderItem[],
): Promise<AxiosResponse> => {
  return api.patch(`/locations/${parentId}/children/`, { locations })
}
